import React, {useEffect, useState} from "react";
import {ACard} from "@atiautomacao/ati-ui-library";
import CardHeader from "@mui/material/CardHeader";
import {Button, Checkbox, Divider, Grid, List, ListItemButton, ListItemIcon, ListItemText} from "@mui/material";

function not(a: readonly string[], b: readonly string[]) {
    return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: readonly string[], b: readonly string[]) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a: readonly string[], b: readonly string[]) {
    return [...a, ...not(b, a)];
}

interface GridProps {
    teleObjectsAvailable: string[];
    setLeftValues: (left: string[]) => void;
    setRightValues: (right: string[]) => void;
    leftValuesToEdit: string[];
    rightValuesToEdit: string[];
}

export default function GridLeftRightSelect({teleObjectsAvailable, setLeftValues, setRightValues, leftValuesToEdit, rightValuesToEdit}: Readonly<GridProps>) {
    const [checked, setChecked] = React.useState<readonly string[]>([]);
    const [left, setLeft] = React.useState<string[]>([]);
    const [right, setRight] = React.useState<string[]>([]);
    const [previousLength, setPreviousLength] = useState(0);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    useEffect(() => {
        if(rightValuesToEdit.length > 0 || leftValuesToEdit.length > 0) {
            setLeft(leftValuesToEdit);
            setRight(rightValuesToEdit);
        }
    }, [leftValuesToEdit, rightValuesToEdit]);

    useEffect(() => {
        if (teleObjectsAvailable.length < previousLength) {
            const updateLists = () => {
                const filteredLeft = intersection(teleObjectsAvailable, left);

                const filteredRight = intersection(teleObjectsAvailable, right);

                setLeft(filteredLeft);
                setRight(filteredRight);
            };

            updateLists();
        } else {
            const newObjects = teleObjectsAvailable.filter(
                (item) => !left.includes(item) && !right.includes(item)
            );
            if (newObjects.length > 0) {
                setLeft([...left, ...newObjects]);
            }
        }

        setPreviousLength(teleObjectsAvailable.length);
        setChecked((prev) => intersection(prev, teleObjectsAvailable));

        if(teleObjectsAvailable.length === 0 && leftValuesToEdit.length === 0 && rightValuesToEdit.length === 0){
            setLeft([]);
            setRight([]);
        }
    }, [teleObjectsAvailable]);

    useEffect(() => {
        setLeftValues(left);
        setRightValues(right);
    }, [left, right]);

    const handleToggle = (value: string) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const numberOfChecked = (items: readonly string[]) =>
        intersection(checked, items).length;

    const handleToggleAll = (items: readonly string[]) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const customList = (title: string, items: readonly string[]) => (
        <ACard styleProps={{ cardStyle:{width: '100%'} }}>
            <CardHeader
                sx={{ px: 2, py: 1 }}
                avatar={
                    <Checkbox
                        onClick={handleToggleAll(items)}
                        checked={numberOfChecked(items) === items.length && items.length !== 0}
                        indeterminate={
                            numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
                        }
                        disabled={items.length === 0}
                        inputProps={{
                            'aria-label': `Selecionar todos os teleobjetos da lista ${title}`,
                        }}
                    />
                }
                title={title}
                subheader={`${numberOfChecked(items)}/${items.length} selecionados`}
            />
            <Divider />
            <List
                sx={{
                    width: 300,
                    height: 230,
                    bgcolor: 'background.paper',
                    overflow: 'auto',
                }}
                dense
                component="div"
            >
                {items.map((value: string) => {
                    const labelId = `transfer-list-all-item-${value}-label`;

                    return (
                        <ListItemButton
                            key={value}
                            onClick={handleToggle(value)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.indexOf(value) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{
                                        'aria-labelledby': labelId,
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={value} />
                        </ListItemButton>
                    );
                })}
            </List>
        </ACard>
    );

    return (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item>{customList('Esquerda', left)}</Grid>
            <Grid item>
                <Grid container direction="column" alignItems="center">
                    <Button
                        sx={{ my: 0.5 }}
                        variant="outlined"
                        size="small"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label="Mover selecionados para a direita"
                    >
                        &gt;
                    </Button>
                    <Button
                        sx={{ my: 0.5 }}
                        variant="outlined"
                        size="small"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="Mover selecionados para a esquerda"
                    >
                        &lt;
                    </Button>
                </Grid>
            </Grid>
            <Grid item>{customList('Direita', right)}</Grid>
        </Grid>
    )
}